import { HttpTypes } from "@medusajs/types";
import { slugUtils } from "./slug-utils";

const FALLBACK_CATEGORY_HANDLE = "autres";

// Construit le segment d'URL "handle-id" à partir d'un produit.
export const getProductSlug = (
  product: Pick<HttpTypes.StoreProduct, "handle" | "id">,
): string => `${product.handle}-${product.id}`;

// Fait l'inverse de getProductSlug : retrouve handle et id à partir du segment d'URL.
// On coupe sur le dernier tiret car les ids Medusa n'en contiennent pas.
export const parseProductSlug = (
  slug: string,
): { handle: string; id: string } | null => {
  const separatorIndex = slug.lastIndexOf("-");

  if (separatorIndex === -1 || separatorIndex === slug.length - 1) {
    return null;
  }

  return {
    handle: slug.slice(0, separatorIndex),
    id: slug.slice(separatorIndex + 1),
  };
};

// Détermine la catégorie et sous-catégorie à utiliser dans l'URL d'un produit :
// on prend la première catégorie qui a un parent également présent dans product.categories.
// Fallback : une seule catégorie trouvée -> dupliquée sur les deux segments ; aucune -> "autres".
export const getProductCategoryPath = (
  product: Pick<HttpTypes.StoreProduct, "categories">,
): { category: string; subCategory: string } => {
  const categories = product.categories ?? [];

  if (categories.length === 0) {
    return {
      category: FALLBACK_CATEGORY_HANDLE,
      subCategory: FALLBACK_CATEGORY_HANDLE,
    };
  }

  const subCategory = categories.find(
    (category) =>
      category.parent_category_id &&
      categories.some(
        (maybeParent) => maybeParent.id === category.parent_category_id,
      ),
  );

  if (subCategory) {
    const parentCategory = categories.find(
      (category) => category.id === subCategory.parent_category_id,
    )!;

    return { category: parentCategory.handle, subCategory: subCategory.handle };
  }

  return {
    category: categories[0]?.handle ?? "",
    subCategory: categories[0]?.handle ?? "",
  };
};

// Slugifie le titre d'une option (ex: "Couleur" -> "couleur") pour en faire une clé de query param.
const optionValueParamKey = (optionTitle: string) => slugUtils(optionTitle);

// Transforme les options sélectionnées (option_id -> valeur) en query params slugifiés,
// ex: { opt_1: 'Rouge', opt_2: 'M' } -> "couleur=rouge&taille=m".
export const buildVariantSearchParams = (
  product: Pick<HttpTypes.StoreProduct, "options">,
  selectedOptions: Record<string, string>,
): URLSearchParams => {
  const params = new URLSearchParams();

  for (const option of product.options ?? []) {
    const value = option.id ? selectedOptions[option.id] : undefined;
    if (!option.title || !value) continue;

    params.set(optionValueParamKey(option.title), slugUtils(value));
  }

  return params;
};

// Fait l'inverse de buildVariantSearchParams : relit les query params de l'URL et retrouve
// les vraies valeurs d'options du produit (option_id -> valeur non slugifiée).
export const resolveOptionsFromSearchParams = (
  product: Pick<HttpTypes.StoreProduct, "options">,
  searchParams: URLSearchParams,
): Record<string, string> => {
  const selectedOptions: Record<string, string> = {};

  for (const option of product.options ?? []) {
    if (!option.id || !option.title) continue;

    const paramValue = searchParams.get(optionValueParamKey(option.title));
    if (!paramValue) continue;

    const matchedValue = option.values?.find(
      (value) => slugUtils(value.value) === paramValue,
    );
    if (matchedValue) {
      selectedOptions[option.id] = matchedValue.value;
    }
  }

  return selectedOptions;
};

// Construit l'URL complète d'un produit : /boutique/produits/[categorie]/[sous-categorie]/[handle-id]
// avec, si fourni, les options sélectionnées en query params.
export const getProductUrl = (
  product: Pick<
    HttpTypes.StoreProduct,
    "handle" | "id" | "categories" | "options"
  >,
  selectedOptions?: Record<string, string>,
): string => {
  const { category, subCategory } = getProductCategoryPath(product);
  const slug = getProductSlug(product);
  const query = selectedOptions
    ? buildVariantSearchParams(product, selectedOptions).toString()
    : "";

  return `/boutique/produits/${category}/${subCategory}/${slug}${query ? `?${query}` : ""}`;
};
